import React, { useEffect, useState } from 'react';
import { useAuth } from './contexts/AuthContext';
import { useAlert } from './contexts/AlertContext';
import apiService from './apiService';
import { DollarSign, Check, X, Clock } from 'lucide-react';

const BillingManagement = () => {
    const { currentUser } = useAuth();
    const { showAlert } = useAlert();
    const [subscriptions, setSubscriptions] = useState([]);
    const [upgradeRequests, setUpgradeRequests] = useState([]);
    const [activeTab, setActiveTab] = useState('subscriptions');
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        try {
            const requests = await apiService.getUpgradeRequests({ status: 'pending' });
            setUpgradeRequests(requests);
        } catch (e) {
            showAlert('Failed to load data', 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleGenerateMonthlyInvoices = async () => {
        try {
            const count = await apiService.generateMonthlyInvoices();
            showAlert(`Generated ${count} monthly invoices successfully`, 'success');
            loadData();
        } catch (e) {
            showAlert('Failed to generate invoices', 'error');
        }
    };

    const handleSuspendOverdueAccounts = async () => {
        try {
            const count = await apiService.suspendOverdueAccounts();
            showAlert(`Suspended ${count} overdue accounts`, count > 0 ? 'warning' : 'info');
        } catch (e) {
            showAlert('Failed to suspend accounts', 'error');
        }
    };

    // Mock subscriptions for display
    const mockSubscriptions = [
        { id: 1, orgName: 'TestAccount', plan: 'hospital', status: 'active', price: 299, modules: ['staff', 'inventory', 'reports'] },
    ];

    const handleApproveRequest = async (requestId) => {
        try {
            await apiService.approveUpgradeRequest({ requestId, adminNotes: 'Approved' });
            showAlert('Upgrade approved. M-Pesa payment request will be sent to organization admin.', 'success');
            setUpgradeRequests(prev => prev.filter(r => r.id !== requestId));
        } catch (e) {
            showAlert('Failed to approve request', 'error');
        }
    };

    const handleRejectRequest = async (requestId) => {
        try {
            await apiService.rejectUpgradeRequest({ requestId, adminNotes: 'Rejected' });
            showAlert('Upgrade request rejected', 'success');
            setUpgradeRequests(prev => prev.filter(r => r.id !== requestId));
        } catch (e) {
            showAlert('Failed to reject request', 'error');
        }
    };

    if (currentUser?.role !== 'superadmin') {
        return <div className="p-6 text-red-600">Access denied.</div>;
    }

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between">
                <div>
                    <h2 className="text-2xl font-bold">Billing & Subscriptions</h2>
                    <p className="text-gray-600 dark:text-gray-400">Manage subscriptions and upgrade requests</p>
                </div>
                <div className="flex gap-2">
                    <button
                        onClick={handleGenerateMonthlyInvoices}
                        className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
                    >
                        Generate Monthly Invoices
                    </button>
                    <button
                        onClick={handleSuspendOverdueAccounts}
                        className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700"
                    >
                        Suspend Overdue
                    </button>
                </div>
            </div>

            {/* Tabs */}
            <div className="flex gap-4 border-b">
                <button
                    onClick={() => setActiveTab('subscriptions')}
                    className={`px-4 py-2 -mb-px ${activeTab === 'subscriptions' ? 'border-b-2 border-blue-500 text-blue-600 font-semibold' : 'text-gray-600'}`}
                >
                    Active Subscriptions
                </button>
                <button
                    onClick={() => setActiveTab('requests')}
                    className={`px-4 py-2 -mb-px ${activeTab === 'requests' ? 'border-b-2 border-blue-500 text-blue-600 font-semibold' : 'text-gray-600'}`}
                >
                    Upgrade Requests {upgradeRequests.length > 0 && <span className="ml-2 bg-red-500 text-white text-xs px-2 py-1 rounded-full">{upgradeRequests.length}</span>}
                </button>
            </div>

            {/* Subscriptions Tab */}
            {activeTab === 'subscriptions' && (
                <div className="bg-white dark:bg-gray-800 rounded-lg overflow-hidden">
                    <table className="w-full text-left">
                        <thead className="bg-gray-50 dark:bg-gray-700">
                            <tr className="border-b">
                                <th className="p-3">Organization</th>
                                <th className="p-3">Plan</th>
                                <th className="p-3">Modules</th>
                                <th className="p-3">Price</th>
                                <th className="p-3">Status</th>
                                <th className="p-3">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {mockSubscriptions.map(sub => (
                                <tr key={sub.id} className="border-b">
                                    <td className="p-3 font-semibold">{sub.orgName}</td>
                                    <td className="p-3 capitalize">{sub.plan}</td>
                                    <td className="p-3">
                                        <div className="flex gap-1 flex-wrap">
                                            {sub.modules.map(m => (
                                                <span key={m} className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded">{m}</span>
                                            ))}
                                        </div>
                                    </td>
                                    <td className="p-3">${sub.price}/mo</td>
                                    <td className="p-3">
                                        <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded">{sub.status}</span>
                                    </td>
                                    <td className="p-3">
                                        <button className="text-blue-600 hover:underline text-sm">Manage</button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Upgrade Requests Tab */}
            {activeTab === 'requests' && (
                <div className="space-y-4">
                    {loading ? (
                        <div className="bg-white dark:bg-gray-800 p-8 rounded-lg text-center">
                            <p>Loading...</p>
                        </div>
                    ) : upgradeRequests.length === 0 ? (
                        <div className="bg-white dark:bg-gray-800 p-8 rounded-lg text-center text-gray-500">
                            <Clock className="w-12 h-12 mx-auto mb-3 opacity-50" />
                            <p>No pending upgrade requests</p>
                        </div>
                    ) : (
                        upgradeRequests.map(req => (
                            <div key={req.id} className="bg-white dark:bg-gray-800 border rounded-lg p-6">
                                <div className="flex items-start justify-between">
                                    <div className="flex-1">
                                        <h4 className="font-semibold text-lg">Organization ID: {req.organizationId}</h4>
                                        <p className="text-sm text-gray-600 dark:text-gray-400">Requested: {new Date(req.requestedAt).toLocaleDateString()}</p>
                                        <div className="mt-3">
                                            <p className="text-sm font-medium text-gray-700 dark:text-gray-300">Modules Requested:</p>
                                            <div className="flex gap-2 mt-2">
                                                {(req.modulesRequested || []).map((m, idx) => (
                                                    <span key={idx} className="bg-blue-100 text-blue-700 px-3 py-1 rounded text-sm">{m}</span>
                                                ))}
                                            </div>
                                        </div>
                                        <p className="text-lg font-bold text-blue-600 mt-3">Total: KES {req.quotedPrice || 0}/month</p>
                                    </div>
                                    <div className="flex gap-2">
                                        <button
                                            onClick={() => handleApproveRequest(req.id)}
                                            className="bg-green-600 text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-green-700"
                                        >
                                            <Check className="w-4 h-4" />
                                            Approve
                                        </button>
                                        <button
                                            onClick={() => handleRejectRequest(req.id)}
                                            className="bg-red-600 text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-red-700"
                                        >
                                            <X className="w-4 h-4" />
                                            Reject
                                        </button>
                                    </div>
                                </div>
                            </div>
                        ))
                    )}
                </div>
            )}
        </div>
    );
};

export default BillingManagement;

